import React, { useState, useEffect } from 'react';
import { Calendar } from 'react-big-calendar';
import ResourceNavigation from './ResourceNavigation';

/**
 * Componente contentor para o calendário da timeline
 * Otimizado para experiência mobile seguindo padrões modernos de design
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.calendarProps - Propriedades para o componente Calendar
 * @param {Object} props.containerRef - Referência para o container do calendário
 * @param {Object} props.touchHandlers - Handlers para eventos de toque
 * @param {boolean} props.isMobile - Se estamos em um dispositivo móvel
 * @param {boolean} props.hasMoreLeft - Se há mais recursos à esquerda
 * @param {boolean} props.hasMoreRight - Se há mais recursos à direita
 * @param {Function} props.onPrevResource - Função para navegar para recursos anteriores
 * @param {Function} props.onNextResource - Função para navegar para recursos seguintes
 * @param {string} props.width - Largura do calendário
 * @param {string} props.height - Altura do calendário
 * @returns {JSX.Element} - Componente React
 */
const CalendarContainer = ({
  calendarProps,
  containerRef,
  touchHandlers,
  isMobile,
  hasMoreLeft,
  hasMoreRight,
  onPrevResource,
  onNextResource,
  width,
  height = 'auto'
}) => {
  // Estado para controlar a visibilidade das setas de navegação
  const [navButtonsVisible, setNavButtonsVisible] = useState(true);

  // Detectar se estamos em um dispositivo com suporte a hover
  const [hasHoverSupport, setHasHoverSupport] = useState(false);

  // Verificar suporte a hover quando o componente monta
  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover)');
    setHasHoverSupport(mediaQuery.matches);
    
    // Timeout para ocultar os botões após 3 segundos em dispositivos móveis
    if (isMobile) {
      const timer = setTimeout(() => {
        setNavButtonsVisible(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  // Handler de movimento do mouse para mostrar os botões
  const handleMouseMove = () => {
    if (!navButtonsVisible) {
      setNavButtonsVisible(true);
    }
    
    // Se estamos em um dispositivo móvel, resetar o timer para ocultar os botões
    if (isMobile) {
      clearTimeout(window.navButtonsTimer);
      window.navButtonsTimer = setTimeout(() => {
        setNavButtonsVisible(false);
      }, 3000);
    }
  };

  // Manipulador para scroll do calendário, para mostrar botões quando o usuário interage
  const handleScroll = () => {
    if (!navButtonsVisible) {
      setNavButtonsVisible(true);
      
      // Esconder novamente após alguns segundos em dispositivos móveis
      if (isMobile) {
        clearTimeout(window.navButtonsTimer);
        window.navButtonsTimer = setTimeout(() => {
          setNavButtonsVisible(false);
        }, 3000);
      }
    }
  };

  return (
    <div 
      className={`calendar-container relative bg-white rounded-lg shadow-md w-full overflow-hidden ${isMobile ? 'mobile-calendar' : ''}`}
      style={{ 
        height: isMobile ? 'calc(100vh - var(--header-height, 60px))' : height, 
        maxWidth: '100vw', 
        padding: 0,
        overflowY: isMobile ? 'hidden' : 'auto', // Bloquear scroll vertical no mobile
        WebkitOverflowScrolling: 'touch' // Melhorar scroll em dispositivos iOS
      }}
      ref={containerRef}
      onTouchStart={(e) => {
        touchHandlers.onTouchStart(e);
        setNavButtonsVisible(true);
      }}
      onTouchMove={(e) => {
        touchHandlers.onTouchMove(e);
        setNavButtonsVisible(true);
      }}
      onTouchEnd={(e) => {
        touchHandlers.onTouchEnd(e);
        // Em dispositivos móveis, esconder após um tempo
        if (isMobile) {
          clearTimeout(window.navButtonsTimer);
          window.navButtonsTimer = setTimeout(() => {
            setNavButtonsVisible(false);
          }, 3000);
        }
      }}
      onMouseMove={handleMouseMove}
      onScroll={handleScroll}
    >
      {/* Indicadores de navegação para mobile/tablet */}
      {width < 1024 && (
        <div 
          className={`${navButtonsVisible ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        >
          <ResourceNavigation 
            hasMoreLeft={hasMoreLeft}
            hasMoreRight={hasMoreRight}
            onPrevious={onPrevResource}
            onNext={onNextResource}
          />
        </div>
      )}

      <Calendar
        {...calendarProps}
        style={{ 
          height: 'auto', 
          maxWidth: '100%', 
          width: '100%', 
          padding: 0,
          touchAction: 'pan-y', // Melhorar comportamento de touch
          paddingLeft: hasMoreLeft ? '8px' : '0', // Pequeno padding para não encobrir eventos
          paddingRight: hasMoreRight ? '8px' : '0'
        }}
        className="show-all-time-slots rbc-calendar-custom p-0"
      />
    </div>
  );
};

export default CalendarContainer; 